import VariantPicker from '@theme/variant-picker';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

class VariantPickerMetafields extends VariantPicker {
  /** @type {Array<{id:number,available:boolean,price:number,sku:string,title:string,size:string|null,colorId:string|null,colorName:string|null}>} */
  #map = [];
  #state = { size: null, color: null };
  /** @type {AbortController|undefined} */
  #fetchAbortController;

  connectedCallback() {
    super.connectedCallback();

    const mapEl = this.querySelector('[data-variant-metafields-map]');
    try { this.#map = JSON.parse(mapEl?.textContent || '[]'); } catch { this.#map = []; }

    this.#state.size = this.dataset.initialSize || null;
    this.#state.color = this.dataset.initialColorId || null;

    this.#updateOptionAvailability();
    this.#updateColorNameLabel(this.#state.color);
    this.#bindSizeSelect();
  }

  #bindSizeSelect() {
    const select = this.querySelector('select[data-mf-key="size"]');
    if (!(select instanceof HTMLSelectElement)) return;
    if (select.dataset.mfBound === 'true') return;

    select.dataset.mfBound = 'true';
    select.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLSelectElement)) return;
      const option = target.selectedOptions[0];
      if (!option) return;

      this.#handleOptionChange('size', option.dataset.mfValue || target.value, option);
    });

    if (this.#state.size) {
      select.value = this.#state.size;
    }
  }

  /** @override */
  variantChanged(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    const key = target.dataset.mfKey;
    const value = target.dataset.mfValue;
    if (!key || !value) return;

    this.#handleOptionChange(key, value, target);
  }

  /**
   * @param {'size'|'color'} key
   * @param {string} value
   * @param {HTMLElement} target
   */
  #handleOptionChange(key, value, target) {
    this.#state[key] = value;

    const variant = this.#resolveVariant();

    this.#updateOptionAvailability();

    if (key === 'color') {
      this.#updateColorNameLabel(value);
    }

    if (!variant) {
      this.#showUnavailable();
      return;
    }

    this.#hideUnavailable();
    this.#updateFormVariantId(variant.id);
    this.#updateUrl(variant.id);

    this.dispatchEvent(new VariantSelectedEvent({ id: String(variant.id) }));
    this.#fetchVariantSection(variant.id);
  }

  /**
   * Strict resolver — variant must have BOTH metafields filled and match the current state values.
   */
  #resolveVariant() {
    const { size, color } = this.#state;
    if (size == null && color == null) return null;

    return this.#map.find((v) => {
      if (size != null) {
        if (v.size == null || v.size === '') return false;
        if (v.size !== size) return false;
      }
      if (color != null) {
        if (v.colorId == null || v.colorId === '') return false;
        if (v.colorId !== color) return false;
      }
      return true;
    }) || null;
  }

  #updateOptionAvailability() {
    const sizeRadios = Array.from(this.querySelectorAll('input[data-mf-key="size"]'));
    const sizeSelect = this.querySelector('select[data-mf-key="size"]');
    const colorInputs = Array.from(this.querySelectorAll('input[data-mf-key="color"]'));
    const { size, color } = this.#state;

    sizeRadios.forEach((input) => {
      const value = input.dataset.mfValue;
      const available = this.#map.some((row) =>
        row.available &&
        row.size === value &&
        (color == null || row.colorId === color)
      );
      this.#applyAvailability(input, available);
    });

    if (sizeSelect instanceof HTMLSelectElement) {
      Array.from(sizeSelect.options).forEach((option) => {
        option.dataset.optionAvailable = String(
          this.#map.some((row) =>
            row.available &&
            row.size === option.value &&
            (color == null || row.colorId === color)
          )
        );
      });
    }

    colorInputs.forEach((input) => {
      const v = input.dataset.mfValue;
      const available = this.#map.some((row) =>
        row.available &&
        row.colorId === v &&
        (size == null || row.size === size)
      );
      this.#applyAvailability(input, available);
    });
  }

  #applyAvailability(input, available) {
    input.dataset.optionAvailable = String(available);
    if (available) {
      input.removeAttribute('aria-disabled');
    } else {
      input.setAttribute('aria-disabled', 'true');
    }

    const label = input.closest('label');
    if (!label) return;

    const isSwatch = label.classList.contains('variant-option__button-label--has-swatch');
    const pill = label.querySelector('[data-key="variant-option-pill"]');
    const strikethrough = label.querySelector('.variant-option__strikethrough');

    if (isSwatch) {
      if (available) {
        strikethrough?.remove();
      } else if (!strikethrough) {
        label.appendChild(this.#createStrikethroughSvg());
      }
      return;
    }

    if (available) {
      if (!pill) {
        const span = document.createElement('span');
        span.className = 'variant-option__button-label__pill';
        span.dataset.key = 'variant-option-pill';
        label.insertBefore(span, label.querySelector('[data-key="variant-option-text"]'));
      }
      strikethrough?.remove();
    } else {
      pill?.remove();
      if (!strikethrough) {
        label.appendChild(this.#createStrikethroughSvg());
      }
    }
  }

  #createStrikethroughSvg() {
    const svgNs = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNs, 'svg');
    svg.setAttribute('viewBox', '0 0 100 46');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    svg.setAttribute('class', 'variant-option__strikethrough');
    svg.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < 2; i++) {
      const line = document.createElementNS(svgNs, 'line');
      line.setAttribute('x1', '100'); line.setAttribute('y1', '0');
      line.setAttribute('x2', '0'); line.setAttribute('y2', '46');
      line.setAttribute('vector-effect', 'non-scaling-stroke');
      svg.appendChild(line);
    }
    return svg;
  }

  #syncStateFromDom() {
    const checkedSize = this.querySelector('input[data-mf-key="size"]:checked');
    const sizeSelect = this.querySelector('select[data-mf-key="size"]');
    const checkedColor = this.querySelector('input[data-mf-key="color"]:checked');

    if (checkedSize instanceof HTMLInputElement && checkedSize.dataset.mfValue) {
      this.#state.size = checkedSize.dataset.mfValue;
    } else if (sizeSelect instanceof HTMLSelectElement && sizeSelect.value) {
      this.#state.size = sizeSelect.value;
    } else if (this.dataset.initialSize) {
      this.#state.size = this.dataset.initialSize;
    }

    if (checkedColor instanceof HTMLInputElement && checkedColor.dataset.mfValue) {
      this.#state.color = checkedColor.dataset.mfValue;
    } else if (this.dataset.initialColorId) {
      this.#state.color = this.dataset.initialColorId;
    }
  }

  #updateColorNameLabel(colorId) {
    const labelEl = this.querySelector('[data-mf-color-name]');
    if (!labelEl) return;

    if (!colorId) {
      labelEl.textContent = '';
      return;
    }

    const checked = this.querySelector(`input[data-mf-key="color"][data-mf-value="${CSS.escape(colorId)}"]:checked`);
    if (checked instanceof HTMLInputElement && checked.dataset.mfColorName) {
      labelEl.textContent = checked.dataset.mfColorName;
      return;
    }

    const fromMap = this.#map.find((v) => v.colorId === colorId);
    if (fromMap?.colorName) {
      labelEl.textContent = fromMap.colorName;
    }
  }

  #updateFormVariantId(variantId) {
    const productId = this.dataset.productId;
    const form = document.querySelector(`product-form-component[data-product-id="${productId}"] form`);
    const input = form?.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement) input.value = String(variantId);
  }

  #updateUrl(variantId) {
    if (this.dataset.templateProductMatch !== 'true') return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));
    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  #showUnavailable() {
    const banner = this.querySelector('[data-mf-unavailable]');
    banner?.classList.remove('hidden');
    this.dispatchEvent(
      new VariantUpdateEvent(null, '', {
        html: new DOMParser().parseFromString('<html><body></body></html>', 'text/html'),
        productId: this.dataset.productId ?? '',
      })
    );
  }

  #hideUnavailable() {
    const banner = this.querySelector('[data-mf-unavailable]');
    banner?.classList.add('hidden');
  }

  async #fetchVariantSection(variantId) {
    this.#fetchAbortController?.abort();
    this.#fetchAbortController = new AbortController();

    const productUrl = (this.dataset.productUrl || '').split('?')[0];
    const sectionId = this.dataset.sectionId;
    const requestUrl = `${productUrl}?variant=${variantId}&section_id=${sectionId}`;

    const localVariant = this.#map.find((v) => String(v.id) === String(variantId));
    const fallbackResource = localVariant
      ? { id: localVariant.id, available: localVariant.available, price: localVariant.price, sku: localVariant.sku, title: localVariant.title }
      : null;

    try {
      const response = await fetch(requestUrl, { signal: this.#fetchAbortController.signal });
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

      const ourPicker = html.querySelector('variant-picker-metafields');
      let variantResource = fallbackResource;

      if (ourPicker) {
        const variantJsonEl = ourPicker.querySelector('script[type="application/json"]:not([data-variant-metafields-map])');
        if (variantJsonEl?.textContent) {
          try {
            const parsed = JSON.parse(variantJsonEl.textContent.trim());
            if (parsed && parsed.id) variantResource = parsed;
          } catch {}
        }

        const newMapEl = ourPicker.querySelector('[data-variant-metafields-map]');
        if (newMapEl?.textContent) {
          try { this.#map = JSON.parse(newMapEl.textContent.trim()); } catch {}
        }

        morph(this, ourPicker, {
          ...MORPH_OPTIONS,
          getNodeKey: (node) => (node instanceof HTMLElement ? node.dataset.key : undefined),
        });

        this.#syncStateFromDom();
        this.#updateOptionAvailability();
        this.#updateColorNameLabel(this.#state.color);
        this.#bindSizeSelect();
      }

      this.dispatchEvent(
        new VariantUpdateEvent(variantResource, String(variantId), {
          html,
          productId: this.dataset.productId ?? '',
        })
      );
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      console.error('[variant-picker-metafields] fetch error', err);
      if (fallbackResource) {
        this.dispatchEvent(
          new VariantUpdateEvent(fallbackResource, String(variantId), {
            html: new DOMParser().parseFromString('<html><body></body></html>', 'text/html'),
            productId: this.dataset.productId ?? '',
          })
        );
      }
    }
  }
}

if (!customElements.get('variant-picker-metafields')) {
  customElements.define('variant-picker-metafields', VariantPickerMetafields);
}
